"use client";

import { c as _c } from "react/compiler-runtime";
import { measureReplicaBarRowNeededPx, replicaBarOverflowState } from "@multi-tenant-cms/builder/ui/lib/replicaMegaMenuHeader";
import { type RefObject, useLayoutEffect, useRef, useState } from "react";

/**
 * Observe the replica bar wrap + nav. Public and canvas share this so a
 * slightly narrow Theme Builder canvas switches to burger when items overflow,
 * instead of wrapping “Who we Help” or waiting for a tiny window width.
 */
export function useReplicaBarOverflow(t0) {
  const $ = _c(5);
  const {
    breakpointDrawer,
    navRef,
    wrapRef
  } = t0;
  const [overflow, setOverflow] = useState(false);
  const overflowRef = useRef(false);
  const neededRef = useRef(0);
  let t1;
  let t2;
  if ($[0] !== breakpointDrawer || $[1] !== navRef || $[2] !== wrapRef) {
    t1 = () => {
      if (breakpointDrawer) {
        overflowRef.current = false;
        neededRef.current = 0;
        setOverflow(false);
        return;
      }
      if (typeof ResizeObserver === "undefined") {
        return;
      }
      let cancelled = false;
      const read = () => {
        if (cancelled) {
          return;
        }
        const wrap = wrapRef.current;
        const nav = navRef.current;
        if (!(wrap && nav) || wrap.clientWidth < 8) {
          return;
        }
        const next = replicaBarOverflowState({
          cachedNeededPx: neededRef.current,
          compactFromOverflow: overflowRef.current,
          navClientWidth: nav.clientWidth,
          navScrollWidth: nav.scrollWidth,
          rowNeededPx: measureReplicaBarRowNeededPx(wrap, nav),
          wrapClientWidth: wrap.clientWidth
        });
        neededRef.current = next.neededPx;
        if (overflowRef.current === next.overflow) {
          return;
        }
        overflowRef.current = next.overflow;
        setOverflow(next.overflow);
      };
      read();
      const observer = new ResizeObserver(read);
      const wrap_0 = wrapRef.current;
      const nav_0 = navRef.current;
      if (wrap_0) {
        observer.observe(wrap_0);
      }
      if (nav_0) {
        observer.observe(nav_0);
      }
      document.fonts?.ready.then(read);
      return () => {
        cancelled = true;
        observer.disconnect();
      };
    };
    t2 = [breakpointDrawer, navRef, wrapRef];
    $[0] = breakpointDrawer;
    $[1] = navRef;
    $[2] = wrapRef;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useLayoutEffect(t1, t2);
  return overflow;
}